import { config } from './config.js';
import { detectAllSegmentIntersections } from './lineIntersection.js';

// Crossing-aware dog-leg refinement for the contour apportionment (V3, gated by
// ENABLE_CONTOUR_MERGE_XASSIGN — a NO-OP otherwise, so the shipped layout is untouched).
//
// The global apportionment lands every union APPROXIMATELY over its children; the small
// residual offsets render as dog-legged union→child drops, and an angled drop is exactly
// what produces a line crossing. So dog-legs and crossings are the same defect. This pass
// straightens them with a HILL-CLIMB scored by the REAL renderer + intersection detector
// (not an in-engine proxy, which under-counts). For each couple, deepest generation first,
// it tries two moves toward the couple's children-midpoint: (A) slide within the FREE
// slack to its row neighbours, and (B) the FULL move, WIDENING the rows to clear (carrying
// descendants so their drops stay straight — width may grow, which is acceptable). It
// re-derives the rendered edges via buildRenderModel and KEEPS a move only if it lowers
// the real crossing count (ties broken by total dog-leg) AND does not balloon any single
// drop past a cap. So a move that straightens a drop but would open a NEW crossing — or a
// big new jog — is rejected; the same gate accepts the widening that fixes 01391/C1373 and
// rejects the symmetric one that would hurt 01370. Deterministic + bounded; overlap-free
// by construction (the widen only ever opens MIN gaps).

// `lockOrder` (Phase 2C): when an established within-generation order is locked, the refinement
// may still STRAIGHTEN drops (slide/widen a couple, nudge a solo parent outward) but must NOT
// change the left-to-right SEQUENCE — that would break the order the user established. We gate
// every acceptance on `orderOk()`, so the order-preserving sweeps still fire (this is exactly
// what fixes the 13180/C1366 half-sib-48 drop a locked layout otherwise leaves dog-legged) while
// the reordering sweeps (adjacent-swap, fan, hub-flank) self-reject. With lockOrder=false the
// gate is a constant true, so the unlocked behaviour is byte-identical.
export function refineCrossingsAwareV3(
  family,
  customPositions,
  buildRenderModel,
  lockOrder = false
) {
  if (!(config.ENABLE_LAYOUT_V3 && config.ENABLE_CONTOUR_MERGE_XASSIGN)) return;
  // _contourApportion seats each single-parent (solo / half-sib) child at its parent's-side
  // sibship edge, with the union centred over the SHARED children. So the two relaxation-era
  // solo passes below (solo-drop straighten + half-sib regroup) are obsolete and actively
  // harmful here — they de-centre the union to drag the solo child dead-under its parent.
  const SOLO_PLACED_BY_CONTOUR = true;
  const MIN = config.VIRTUAL_X_REQUIRED_BETWEEN_PEOPLE || 1;
  const persons = family.persons;
  if (!persons || persons.length === 0) return;

  // Unions whose junction the renderer should anchor over the CHILDREN's midpoint instead
  // of the partner midpoint (desktop-style ASYMMETRIC bar — see the park pass at the end).
  // Reset every run; buildRenderModel treats a missing/empty set as "all midpoint".
  family.asymJunctionsV3 = new Set();

  // Refine telemetry (cheap counters, stashed on the family; the parallel path's dev log
  // surfaces them): how many trial evaluations the hill-climb paid and how many moves it
  // kept — the data that says whether a slow real-family refine is evaluation-count-bound
  // (messier layout → more candidates) or per-evaluation-bound.
  const stats = { measures: 0, accepted: 0 };
  family._refineStatsV3 = stats;

  // Snapshot virtualX so any failure restores the apportionment's output verbatim.
  const snapshotAll = new Map(persons.map((p) => [p.pid, p.virtualX]));
  const restoreAll = () => {
    for (const p of persons) p.virtualX = snapshotAll.get(p.pid);
    family.convertToCoordinates();
  };

  // Per-generation left-to-right pid sequence (by virtualX). When a locked order is in force,
  // any accepted move must leave this UNCHANGED — that is the lock invariant.
  const orderSeq = () => {
    const byGen = new Map();
    for (const p of persons) {
      const g = p.virtualY ?? 0;
      if (!byGen.has(g)) byGen.set(g, []);
      byGen.get(g).push(p);
    }
    return [...byGen.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(
        ([g, arr]) =>
          g +
          ':' +
          arr
            .sort((a, b) => a.virtualX - b.virtualX || (a.pid < b.pid ? -1 : 1))
            .map((p) => p.pid)
            .join(',')
      )
      .join('|');
  };
  const lockedSeq = lockOrder ? orderSeq() : null;
  const orderOk = () => !lockOrder || orderSeq() === lockedSeq;

  try {
    // Couples (parent pair / single parent) with their shared children, matching the
    // render's union grouping (layout.js sharedChildren).
    const couples = [];
    const seen = new Set();
    for (const p of persons) {
      const kids = p.children || [];
      if (kids.length === 0) continue;
      let recorded = false;
      for (const sp of p.spouses || []) {
        const shared = kids.filter(
          (c) => c.motherId === sp.pid || c.fatherId === sp.pid
        );
        if (shared.length === 0) continue;
        const key = [p.pid, sp.pid].sort((a, b) => (a < b ? -1 : 1)).join(':');
        if (seen.has(key)) {
          recorded = true;
          continue;
        }
        seen.add(key);
        recorded = true;
        if ((p.virtualY ?? 0) === (sp.virtualY ?? 0)) {
          couples.push({ parents: [p, sp], children: shared });
        }
      }
      if (!recorded) couples.push({ parents: [p], children: kids });
    }
    if (couples.length === 0) return;

    // SOLO drops: a child of a COUPLED parent whose other parent is absent (a
    // single-parent child of someone who is also married). The render draws its drop
    // from that one parent, so it should sit straight UNDER that parent — but the couple
    // grouping above only sees the parent's SHARED children, so this drop is otherwise
    // never straightened (01366: 48 hangs off 49 alone while 49 sits over its union
    // with 44). Tracked separately so the dog-leg metric sees it and a dedicated move can
    // pull the child (carrying its own subtree) under the parent.
    const soloDrops = [];
    for (const c of persons) {
      const parent =
        c.father && !c.mother
          ? c.father
          : c.mother && !c.father
          ? c.mother
          : null;
      if (!parent) continue; // 0 or 2 drawn parents
      if (!(parent.spouses && parent.spouses.length)) continue; // uncoupled → in couples
      soloDrops.push({ parent, child: c });
    }

    const pairKey = (a, b) => (a < b ? `${a}:${b}` : `${b}:${a}`);
    const childMidVX = (cp) => {
      const xs = cp.children.map((c) => c.virtualX);
      return (Math.min(...xs) + Math.max(...xs)) / 2;
    };

    // The RENDERED union x, in virtual units. For a normal (≤2-spouse, or first-per-side)
    // couple the union is the partner midpoint. But for an EXTRA spouse — the 2nd+ spouse
    // on one side of a 3+-spouse hub — the renderer places the union at
    // `previousSpouse.x ± EXTRA_SPOUSE_CHILD_X_STEP`, clamped along the (diagonal) marriage
    // line (layout.js extraSpouseInfo). Measuring the MIDPOINT here made every straighten
    // pass aim children at a point the renderer never uses (G-1-01406: Auto-arrange made
    // the u(1,13)→12 dog-leg BIGGER because the metric and the drawing disagreed). This
    // helper replicates the renderer's rule over the CURRENT virtual positions.
    const EXTRA_STEP_VX =
      (config.EXTRA_SPOUSE_CHILD_X_STEP ?? 500) / (config.NODE_GAP || 800);
    const extraPrevSpouse = (hub, sp) => {
      // Replicates layout.js's per-side classification: spouses of a 3+-spouse hub are
      // split left/right of the hub and sorted closest-first; index ≥ 1 on a side is an
      // EXTRA spouse whose union anchors off the PREVIOUS spouse on that side.
      if ((hub.spouses || []).length < 3) return null;
      const side = (hub.spouses || [])
        .filter((s) =>
          sp.virtualX < hub.virtualX
            ? s.virtualX < hub.virtualX
            : s.virtualX >= hub.virtualX
        )
        .sort((a, b) =>
          sp.virtualX < hub.virtualX
            ? b.virtualX - a.virtualX
            : a.virtualX - b.virtualX
        );
      const idx = side.findIndex((s) => s.pid === sp.pid);
      return idx >= 1 ? side[idx - 1] : null;
    };
    const unionVX = (cp) => {
      if (cp.parents.length === 2) {
        const [p, s] = cp.parents;
        // An ASYM-anchored union (the park pass, or the HUB-ASYM ANCHOR below —
        // Tim's ruling: "the asymmetry we accepted in the spacing between parents
        // to preference straight drops applies to multiple spouse scenarios too"):
        // the junction follows the children's midpoint along the bar. Checked
        // BEFORE the extra-spouse anchor so a marked extra-spouse union anchors
        // over its children too; the park pass never marks extra-spouse unions,
        // so pre-existing behaviour is unchanged for unmarked families.
        if (family.asymJunctionsV3.has(pairKey(p.pid, s.pid))) {
          const mid = childMidVX(cp);
          const lineT =
            s.virtualX !== p.virtualX
              ? (mid - p.virtualX) / (s.virtualX - p.virtualX)
              : 0.5;
          // Clamp to an INTERIOR band (2026-07-12; was the full [0,1], and
          // [0.05,0.95] before that): a junction AT a partner's column reads
          // clinically as a single-parent drop — Tim's rule 2 (SS01/G-1-01430,
          // t=1 parked u(2,3) dead under 3 on a locked add). The band keeps
          // ≥ ASYM_JUNCTION_PARENT_CLEARANCE symbol-widths from each partner
          // (in vx: px ÷ NODE_GAP), or the bar midpoint when the bar is too
          // short to hold it. Must mirror layout.js's asym render clamp
          // exactly or the metric aims where the renderer never draws (the
          // G-1-01406 lesson).
          const minOffVX =
            ((config.ASYM_JUNCTION_PARENT_CLEARANCE ?? 0.75) *
              (config.SYMBOL_SIZE || 155)) /
            (config.NODE_GAP || 800);
          const spanVX = Math.abs(s.virtualX - p.virtualX);
          const tBand = spanVX > 2 * minOffVX ? minOffVX / spanVX : 0.5;
          const t = Math.max(tBand, Math.min(1 - tBand, lineT));
          return p.virtualX + t * (s.virtualX - p.virtualX);
        }
        for (const [hub, sp] of [
          [p, s],
          [s, p]
        ]) {
          const prev = extraPrevSpouse(hub, sp);
          if (prev) {
            const px = hub.virtualX;
            const sx = sp.virtualX;
            const dir = sx > px ? 1 : -1;
            const targetX = prev.virtualX + dir * EXTRA_STEP_VX;
            const lineT = sx !== px ? (targetX - px) / (sx - px) : 0.5;
            const tClamped = Math.max(0.05, Math.min(0.95, lineT));
            return px + tClamped * (sx - px);
          }
        }
      }
      return cp.parents.reduce((t, p) => t + p.virtualX, 0) / cp.parents.length;
    };
    // The dog-leg metric scores ONLY union-over-shared-children centring. Solo (single-
    // parent) drops are deliberately EXCLUDED under the contour: it seats them at the
    // parent's-side edge, so a small solo drop is expected and acceptable. Counting it here
    // let the swap/move sweeps "win" dog-leg by dragging a solo child dead-under its parent
    // at the cost of de-centring the couple's union (13180: swapping shared sib 1 past 48).
    const totalDogLeg = () => {
      let d = 0;
      for (const cp of couples) d += Math.abs(unionVX(cp) - childMidVX(cp));
      if (!SOLO_PLACED_BY_CONTOUR)
        for (const s of soloDrops)
          d += Math.abs(s.parent.virtualX - s.child.virtualX);
      return d;
    };
    const maxDogLeg = () => {
      let m = 0;
      for (const cp of couples)
        m = Math.max(m, Math.abs(unionVX(cp) - childMidVX(cp)));
      if (!SOLO_PLACED_BY_CONTOUR)
        for (const s of soloDrops)
          m = Math.max(m, Math.abs(s.parent.virtualX - s.child.virtualX));
      return m;
    };
    // A marriage line running OVER a same-row node (the parallel/overlapping-line
    // defect — nearly as bad as a crossing, and invisible to the edge-edge detector).
    // Counted in engine units, EXCLUDING the intentionally stretched containment bridges
    // (a doubly cross-linked family's secondary cross-marriage, rendered on its own lane).
    // Folded into the defect score below so the hill-climb won't, e.g., swap a plain
    // sibling INTO a spine couple to shave a dog-leg (G-1-01373: 15 between 2 and 3).
    const overBridges = family.containmentBridgesV3 || new Set();
    const marriageOverCount = () => {
      let n = 0;
      const seen = new Set();
      for (const p of persons) {
        for (const sp of p.spouses || []) {
          const key = pairKey(p.pid, sp.pid);
          if (seen.has(key)) continue;
          seen.add(key);
          if (overBridges.has(key)) continue;
          if ((p.virtualY ?? 0) !== (sp.virtualY ?? 0)) continue;
          const lo = Math.min(p.virtualX, sp.virtualX);
          const hi = Math.max(p.virtualX, sp.virtualX);
          if (hi - lo < MIN + 1e-9) continue; // adjacent couple — nothing fits between
          for (const q of persons) {
            if (q === p || q === sp) continue;
            if ((q.virtualY ?? 0) !== (p.virtualY ?? 0)) continue;
            if (q.virtualX > lo + 1e-6 && q.virtualX < hi - 1e-6) {
              n++;
              break;
            }
          }
        }
      }
      return n;
    };
    const crossCount = () => {
      family.convertToCoordinates();
      // B3a: scoring-only render model (skip SVG path strings + clinical data spread).
      const { edges, nodesById } = buildRenderModel(
        family,
        customPositions,
        true
      );
      const m = detectAllSegmentIntersections(edges, nodesById);
      return m ? m.size ?? 0 : 0;
    };
    // Marriage bar widths (|Δvx|) keyed by pair. A candidate that moves one
    // partner without the other stretches a bar that neither the dog metric
    // (union vs childMid) nor the over count (needs a node strictly between)
    // can see — guard explicitly where a candidate can tear a couple. With
    // `sameRowOnly`, cross-row (diagonal) marriages are skipped; the widen's
    // pays-for-itself gate measures ALL pairs (C1458's inherent 55-14 diagonal
    // sits at its ratchet cap — an invisible horizontal stretch breached it).
    const marriageSpans = (sameRowOnly) => {
      const spans = new Map();
      const seenM = new Set();
      for (const q of persons) {
        for (const s2 of q.spouses || []) {
          const k2 = pairKey(q.pid, s2.pid);
          if (seenM.has(k2)) continue;
          seenM.add(k2);
          if (sameRowOnly && (q.virtualY ?? 0) !== (s2.virtualY ?? 0)) continue;
          spans.set(k2, Math.abs(q.virtualX - s2.virtualX));
        }
      }
      return spans;
    };
    const measure = () => {
      stats.measures++;
      return {
        cross: crossCount(),
        over: marriageOverCount(),
        dog: totalDogLeg()
      };
    };
    // The WHOLE main hill-climb is scored on (crossings, then dog-leg) exactly as before
    // — byte-identical behaviour for every family that isn't doubly cross-linked. The
    // marriage-line-over-node count is handled ONLY by a dedicated final de-wedge pass
    // (below), never folded into the main metric: a greedy per-move over-penalty would
    // block the transient line-over-node C1391's union-30-31 straightening passes through.
    const isBetterDog = (cand, ref) =>
      cand.cross < ref.cross ||
      (cand.cross === ref.cross && cand.dog < ref.dog - 1e-6);

    const byPidMap = new Map(persons.map((p) => [p.pid, p]));
    const snapAll = () => new Map(persons.map((p) => [p.pid, p.virtualX]));
    const restoreSnap = (snap) => {
      for (const p of persons) p.virtualX = snap.get(p.pid);
    };
    // A person + its whole descendant subtree (carrying each descendant's spouse so
    // couples move as a unit) — what must travel with a pushed node so its drops stay
    // straight. Mirrors the engine's collectDescendants.
    const descendantsOf = (person) => {
      const out = new Set([person.pid]);
      const stack = [person];
      while (stack.length) {
        const p = stack.pop();
        for (const c of p.children || []) {
          if (!out.has(c.pid)) {
            out.add(c.pid);
            stack.push(c);
          }
        }
        if (p.pid !== person.pid) {
          for (const sp of p.spouses || []) {
            if (!out.has(sp.pid)) {
              out.add(sp.pid);
              stack.push(sp);
            }
          }
        }
      }
      return out;
    };
    // Widen each row top-down to restore MIN gaps after a move: push the right-hand
    // node (and its descendant subtree, so its drops stay straight) right just enough.
    // Order-preserving; only ever opens space (width may grow — acceptable).
    const resolveDownWiden = () => {
      const gens = [...new Set(persons.map((p) => p.virtualY ?? 0))].sort(
        (a, b) => a - b
      );
      for (const g of gens) {
        const row = persons
          .filter((p) => (p.virtualY ?? 0) === g)
          .sort((a, b) => a.virtualX - b.virtualX || (a.pid < b.pid ? -1 : 1));
        for (let i = 1; i < row.length; i++) {
          const need = row[i - 1].virtualX + MIN - row[i].virtualX;
          if (need > 1e-9) {
            for (const pid of descendantsOf(row[i])) {
              byPidMap.get(pid).virtualX += need;
            }
          }
        }
      }
    };

    // PURE tower of a partner: itself + its ancestors + their childless-leaf other
    // children; null when entangled (a remarried ancestor / a non-leaf sibling). Shared
    // by the tower-carrying couple move, the TOWER-YIELD and the HUB-UNION-SPACING moves.
    const pureTowerOf = (p) => {
      const out = new Set([p.pid]);
      const stack = [p];
      while (stack.length) {
        const cur = stack.pop();
        for (const anc of [cur.father, cur.mother]) {
          if (!anc) continue;
          const co = anc === cur.father ? cur.mother : cur.father;
          if ((anc.spouses || []).some((s) => !co || s.pid !== co.pid))
            return null; // remarried ancestor — entangled
          if (out.has(anc.pid)) continue;
          out.add(anc.pid);
          stack.push(anc);
          for (const c of anc.children || []) {
            if (c.pid === cur.pid || out.has(c.pid)) continue;
            const leaf =
              (c.children || []).length === 0 && (c.spouses || []).length === 0;
            if (!leaf) return null; // non-leaf other child — entangled
            out.add(c.pid);
          }
        }
      }
      return out;
    };

    let best = measure();
    if (best.cross === 0 && best.over === 0 && best.dog < 1e-6) return; // perfect
    // A crossing-reducing widen must not, in exchange, balloon another drop into a big
    // dog-leg. Cap the worst single dog-leg at max(initial worst, 4u): moves may open a
    // jog up to 4u to clear a crossing, but never a worse one than the layout already had.
    const dogCap = Math.max(maxDogLeg(), 4);

    // Deepest parent-generation first: a couple's children are final before it moves,
    // and its own parents re-centre over it on a shallower pass.
    const ordered = [...couples].sort(
      (a, b) =>
        Math.max(...b.parents.map((p) => p.virtualY ?? 0)) -
        Math.max(...a.parents.map((p) => p.virtualY ?? 0))
    );

    const SWEEPS = config.MAX_ITERATIONS_FOR_V3_RELAYOUT || 8;
    for (let s = 0; s < SWEEPS; s++) {
      let improved = false;
      for (const cp of ordered) {
        const movers = cp.parents;
        const g = movers[0].virtualY ?? 0;
        const row = persons
          .filter((p) => (p.virtualY ?? 0) === g)
          .sort((a, b) => a.virtualX - b.virtualX || (a.pid < b.pid ? -1 : 1));
        const moverSet = new Set(movers.map((p) => p.pid));
        const idxs = [];
        row.forEach((p, i) => {
          if (moverSet.has(p.pid)) idxs.push(i);
        });
        const first = idxs[0];
        const last = idxs[idxs.length - 1];
        if (last - first + 1 !== idxs.length) continue; // partners not contiguous
        const minX = row[first].virtualX;
        const maxX = row[last].virtualX;
        const leftRoom =
          first > 0 ? minX - row[first - 1].virtualX - MIN : Infinity;
        const rightRoom =
          last < row.length - 1
            ? row[last + 1].virtualX - maxX - MIN
            : Infinity;
        const desired = childMidVX(cp) - unionVX(cp);
        if (Math.abs(desired) < 1e-9) continue;

        // Candidate A — slide within the FREE slack (no widening).
        const clamped = Math.max(-leftRoom, Math.min(rightRoom, desired));
        // Candidate B — the FULL move onto the children's midpoint, then widen the
        // rows to clear (carrying descendants). Lets a jammed couple straighten by
        // making room (width grows) — kept only if it doesn't add a crossing.
        // Candidate T — the FULL move CARRYING both partners' PURE ancestor towers
        // (TGCLOUD-1468 follow-up, families 01406/01407/01409): a plain couple move is
        // rejected whenever the parents carry towers — the towers stay put, de-centre,
        // and cancel the dog-leg win. Moving couple + towers as one rigid unit keeps the
        // towers centred while the union lands on its child (the parents-over-a-multi-
        // spouse-proband elbow). Skipped when either tower is entangled.
        const candidates = [];
        if (Math.abs(clamped) > 1e-9)
          candidates.push({ shift: clamped, widen: false });
        if (Math.abs(desired - clamped) > 1e-9)
          candidates.push({ shift: desired, widen: true });
        if (
          config.ENABLE_PARTNER_TOWER_SLIDE !== false &&
          movers.length === 2
        ) {
          const tA = pureTowerOf(movers[0]);
          const tB = pureTowerOf(movers[1]);
          if (tA && tB && tA.size + tB.size > 2) {
            candidates.push({ shift: desired, widen: true, towers: [tA, tB] });
          }
        }

        for (const c of candidates) {
          const before = snapAll();
          for (const p of movers) p.virtualX += c.shift;
          if (c.towers)
            for (const t of c.towers)
              for (const pid of t) {
                if (moverSet.has(pid)) continue; // parents already shifted above
                const n = byPidMap.get(pid);
                if (n) n.virtualX += c.shift;
              }
          if (c.widen) resolveDownWiden();
          const cand = measure();
          const better = isBetterDog(cand, best) && orderOk();
          if (better && maxDogLeg() > dogCap + 1e-6) {
            restoreSnap(before); // crossing win, but it ballooned a drop — reject
            continue;
          }
          if (better) {
            best = cand;
            improved = true;
            stats.accepted++;
            break;
          }
          restoreSnap(before);
        }
      }

      // CHILD-ALIGN CHAIN (G-1-01413): the sweep above moves PARENTS onto their
      // children's midpoint, but when the parents are jammed (each pinned under its
      // own tower, neighbours tight) the symmetric cheap fix is moving the CHILD
      // under the parents' union instead — carrying the child's own descent so its
      // union stays straight: the child's sole partner keeps its own anchor, and
      // their shared children (each with its whole descent subtree and pendant
      // spouses) shift by δ/2, keeping the junction over them. Gated to an ONLY
      // child (a sibling move would tear its sibship bar) with at most one same-row
      // spouse (hubs have their own machinery), spouses below must be unanchored
      // (no drawn parents). Pareto + same-row clearance + orderOk + dog-cap, like
      // every other candidate — so it fires only where it strictly improves.
      if (config.ENABLE_CHILD_ALIGN_CHAIN !== false)
        for (const cp of ordered) {
          if (!cp.children || cp.children.length !== 1) continue;
          const S = cp.children[0];
          const delta = unionVX(cp) - S.virtualX;
          if (Math.abs(delta) < 1e-6) continue;
          const sSpouses = (S.spouses || []).filter(
            (t) => (t.virtualY ?? 0) === (S.virtualY ?? 0)
          );
          if (sSpouses.length > 1) continue;
          if (sSpouses.length === 0 && (S.children || []).length > 0) continue;
          const follow = new Set();
          let entangled = false;
          const carry = (node) => {
            if (entangled || follow.has(node.pid)) return;
            follow.add(node.pid);
            for (const sp of node.spouses || []) {
              if (sp.pid === S.pid || follow.has(sp.pid)) continue;
              if (sp.father || sp.mother) {
                entangled = true;
                return;
              }
              carry(sp);
            }
            for (const ch of node.children || []) carry(ch);
          };
          if (sSpouses.length === 1)
            for (const ch of S.children || []) {
              carry(ch);
              if (entangled) break;
            }
          if (entangled) continue;
          const before = snapAll();
          S.virtualX += delta;
          for (const pid of follow) {
            const n = byPidMap.get(pid);
            if (n) n.virtualX += delta / 2;
          }
          const rowsTouched = new Set([S.virtualY ?? 0]);
          for (const pid of follow) {
            const n = byPidMap.get(pid);
            if (n) rowsTouched.add(n.virtualY ?? 0);
          }
          const clear = [...rowsTouched].every((g) => {
            const row = persons
              .filter((p) => (p.virtualY ?? 0) === g)
              .sort((a, b) => a.virtualX - b.virtualX);
            for (let i = 1; i < row.length; i++)
              if (row[i].virtualX - row[i - 1].virtualX < MIN - 1e-6)
                return false;
            return true;
          });
          const cand = measure();
          if (
            clear &&
            isBetterDog(cand, best) &&
            orderOk() &&
            maxDogLeg() <= dogCap + 1e-6
          ) {
            best = cand;
            improved = true;
          } else {
            restoreSnap(before);
          }
        }

      // TOWER-YIELD (TGCLOUD-1468 / G-1-01396): a couple whose straightening move is
      // BLOCKED by the adjacent PURE ancestor tower of a neighbouring couple's partner
      // makes that tower YIELD — slide the owning partner + its whole tower away from its
      // spouse by the shortfall (widening that marriage), then complete the blocked move.
      // This is exactly the user's manual fix ("move 23 + 26-27 right to give 24-25 room
      // to centre over 22"). One ATOMIC candidate, Pareto-gated on the real detector
      // (cross ≤, over ≤, dog <) + orderOk — so it fires only where an actual blocked
      // dog-leg exists and can never regress. The tower must be PURE (each ancestor
      // married only within the tower, other children childless leaves) or the move is
      // skipped as entangled.
      if (config.ENABLE_PARTNER_TOWER_SLIDE !== false)
        for (const cp of ordered) {
          const desired = childMidVX(cp) - unionVX(cp);
          if (Math.abs(desired) < 1e-6) continue;
          const dir = Math.sign(desired);
          const movers = cp.parents;
          const g = movers[0].virtualY ?? 0;
          const row = persons
            .filter((p) => (p.virtualY ?? 0) === g)
            .sort(
              (a, b) => a.virtualX - b.virtualX || (a.pid < b.pid ? -1 : 1)
            );
          const moverSet = new Set(movers.map((p) => p.pid));
          const idxs = [];
          row.forEach((p, i) => {
            if (moverSet.has(p.pid)) idxs.push(i);
          });
          if (idxs.length !== movers.length) continue;
          const first = idxs[0];
          const last = idxs[idxs.length - 1];
          if (last - first + 1 !== idxs.length) continue; // partners not contiguous
          const bIdx = dir > 0 ? last + 1 : first - 1;
          if (bIdx < 0 || bIdx >= row.length) continue; // nothing blocks — A/B covered it
          const edgeMover = dir > 0 ? row[last] : row[first];
          const shortfall =
            Math.abs(desired) -
            (Math.abs(row[bIdx].virtualX - edgeMover.virtualX) - MIN);
          if (shortfall < 1e-6) continue; // enough slack — candidate A covered it
          const blocker = row[bIdx];
          // The blocker must be an ancestor inside somebody's PURE tower. Walk DOWN its
          // sole non-leaf descendant chain and take the FIRST descendant whose own pure
          // tower contains the blocker — that partner (e.g. 23, for blocker 27) is the
          // one who yields, carrying its whole tower with it.
          let owner = null;
          let tower = null;
          {
            let cur = blocker;
            for (let hops = 0; hops < 16 && cur; hops++) {
              const nonLeaf = (cur.children || []).filter(
                (c) => (c.children || []).length || (c.spouses || []).length
              );
              if (nonLeaf.length !== 1) break;
              cur = nonLeaf[0];
              const t = pureTowerOf(cur);
              if (t && t.has(blocker.pid)) {
                owner = cur;
                tower = t;
                break;
              }
            }
          }
          if (!owner || !tower) continue;
          if (moverSet.has(owner.pid)) continue;
          if (movers.some((p) => tower.has(p.pid))) continue;
          // A HUB owner (multiple spouses) may yield too — WITH COMPENSATION: each of its
          // OTHER currently-straight unions holds its midpoint by moving the free (single-
          // married) spouse the opposite way, so yielding never un-straightens a sibling
          // union (the G-1-01409 three-union tug-of-war: yielding 3 frees 4-5 to centre
          // over 2, straightens u(2,3) over the proband as a co-benefit, and spouse 11
          // slides to hold u(3,11) over 10 — the on-paper exact solution). A doglegged
          // other-union is left FREE so the move can improve it; an entangled one (spouse
          // not single-married) blocks the yield as before.
          const yieldShift = dir * shortfall;
          const compensations = [];
          {
            let blocked = false;
            for (const c2 of couples) {
              if (c2 === cp || c2.parents.length !== 2) continue;
              if (!c2.parents.some((pp) => pp.pid === owner.pid)) continue;
              if (!c2.children.length) continue;
              const off = Math.abs(unionVX(c2) - childMidVX(c2));
              if (off > 0.01) continue; // doglegged — leave free to improve
              const sp2 = c2.parents.find((pp) => pp.pid !== owner.pid);
              if (!sp2 || tower.has(sp2.pid)) continue; // co-parent inside the tower moves anyway
              // Never compensate by moving the CHILD TARGET of the blocked couple (e.g.
              // 01396: holding u(22,23) by sliding 22 — the very node cp(24,25) is trying
              // to centre over — makes the yield chase a retreating target). Leave that
              // union free; later sweeps re-straighten it.
              if (cp.children.some((c) => c.pid === sp2.pid)) continue;
              if ((sp2.spouses || []).length !== 1) {
                blocked = true; // can't compensate through another hub — skip the yield
                break;
              }
              compensations.push(sp2);
            }
            if (blocked) continue;
          }
          // ATOMIC candidate: tower yields by the shortfall, movers complete the full
          // move, widen to clear anything the tower ran into, then judge for real.
          const before = snapAll();
          for (const pid of tower) {
            const n = byPidMap.get(pid);
            if (n) n.virtualX += yieldShift;
          }
          for (const sp2 of compensations) sp2.virtualX -= yieldShift;
          for (const p of movers) p.virtualX += desired;
          resolveDownWiden();
          const cand = measure();
          if (
            cand.cross <= best.cross &&
            cand.over <= best.over &&
            cand.dog < best.dog - 1e-6 &&
            maxDogLeg() <= dogCap + 1e-6 &&
            orderOk()
          ) {
            best = cand;
            improved = true;
          } else restoreSnap(before);
        }

      // HUB-UNION-SPACING (multi-spouse hubs, families 01406/01407/01409): a hub with two
      // or more CHILD-BEARING same-row unions needs its consecutive union MIDPOINTS at
      // least MIN apart — closer than that, the unions' children physically cannot both
      // sit centred (01406: mids 2.0 and 2.5 with kids needing a full slot). Shift the
      // outer union's NON-HUB spouse outward (carrying its pure tower) by twice the
      // deficit so the midpoints separate; the hub-remarriage child straighten (the final
      // pass below) then drops each union's children onto its midpoint. Pareto-gated on
      // the real detector + orderOk, like every other move here.
      if (config.ENABLE_PARTNER_TOWER_SLIDE !== false)
        for (const H of persons) {
          const hubUnions = couples
            .filter(
              (cp) =>
                cp.parents.length === 2 &&
                cp.children.length &&
                cp.parents.some((p) => p.pid === H.pid) &&
                cp.parents.every((p) => (p.virtualY ?? 0) === (H.virtualY ?? 0))
            )
            .sort((a, b) => unionVX(a) - unionVX(b));
          if (hubUnions.length < 2) continue;
          for (let i = 1; i < hubUnions.length; i++) {
            const gap = unionVX(hubUnions[i]) - unionVX(hubUnions[i - 1]);
            const deficit = MIN - gap;
            if (deficit < 1e-6) continue;
            // Only when the tight midpoints are actually HURTING — one of the two unions'
            // children sit off their midpoint. A harmlessly-tight corpus hub is left alone.
            const hurting =
              Math.abs(
                unionVX(hubUnions[i - 1]) - childMidVX(hubUnions[i - 1])
              ) > 0.05 ||
              Math.abs(unionVX(hubUnions[i]) - childMidVX(hubUnions[i])) > 0.05;
            if (!hurting) continue;
            // Move the union whose non-hub spouse sits OUTWARD of the hub, away from the
            // other union — the left union's spouse further left, or the right's right.
            const pick = (cp, dir) => {
              const sp = cp.parents.find((p) => p.pid !== H.pid);
              if (!sp || (sp.spouses || []).length !== 1) return null;
              // Only when the spouse is on the dir side of the hub (a flanked spouse) —
              // else the shift would drag the union the wrong way.
              if (Math.sign(sp.virtualX - H.virtualX) !== dir) return null;
              const t = pureTowerOf(sp);
              return t ? { sp, t, dir } : null;
            };
            const move = pick(hubUnions[i - 1], -1) || pick(hubUnions[i], 1);
            if (!move) continue;
            const before = snapAll();
            const shift = move.dir * 2 * deficit; // spouse moves 2Δ → midpoint moves Δ
            for (const pid of move.t) {
              const n = byPidMap.get(pid);
              if (n) n.virtualX += shift;
            }
            resolveDownWiden();
            const cand = measure();
            if (
              cand.cross <= best.cross &&
              cand.over <= best.over &&
              cand.dog <= best.dog + 1e-6 && // spacing move: dog may be neutral here —
              // the win lands when the final child-straighten re-centres the children
              maxDogLeg() <= dogCap + 1e-6 &&
              orderOk()
            ) {
              best = cand;
              improved = true;
            } else restoreSnap(before);
          }
        }

      // ROW-SUFFIX WIDEN (G-1-01414): a generation packed at 100% density (01414: 25
      // people, every neighbour exactly MIN apart) leaves NO slack for any surgical
      // straighten — candidate B's resolveDownWiden drags each pushed neighbour's whole
      // descent sideways while the ancestor unions stay behind, so every widen is
      // measured with the rest of the family torn (58:60's +2.5 widen: cross 4→12) and
      // Pareto-rejects; the hill-climb is stuck in a local minimum. The user's manual fix
      // is "move blocks right to create space": open the couple's full deficit as a GAP
      // at the couple's column across the WHOLE family in ONE atomic candidate —
      //   (1) suffix-shift the couple's row from the couple outward by the deficit
      //       (uniform, so no sibship on the row is torn apart internally),
      //   (2) cascade DOWN: every couple whose union ANCHOR moved (partner midpoint,
      //       extra-spouse step or parked anchor alike — unionVX) gets its children
      //       re-centred under it, carrying pendant founder spouses; jammed unions that
      //       were ALREADY off get straightened along the way (01414: 52-54 under
      //       u(27,51), 38 under u(32,39)); row clearance restored in ORIGINAL order,
      //   (3) cascade UP: single-married same-row ancestor couples whose children's
      //       midpoint moved re-centre over it (56:57 follows its widened sibship,
      //       23:26 / 24:30 / 40:41 follow their suffix-shifted children, 6:7 follows
      //       one row further up).
      // Order-preserving by construction (uniform suffix + original-order clearance
      // walks). Originally accepted only when it strictly REDUCED crossings — plus
      // the usual ladder: over ≤, dog <, explicit same-row clearance, dog-cap, orderOk.
      // **DOG-MODE (G-1-01391, 2026-07-11, product rule: "go WIDER and centre the
      // children under the parents"):** the same atomic candidate now also fires on a
      // strict total-DOG-LEG reduction with crossings and over NOT WORSE. 01391's
      // u(46-47)/u(48-49) both sat exactly +0.5 off their children (44/45) with the
      // whole 46..55 suffix needing the shift and the 50/51/14/15/12/9/10/11 chain
      // re-centring behind it — this candidate built exactly that but the crossings-
      // only gate rejected it (crossings stay at 2). Width growth is the preferred
      // cost. SCOPED to families that still HAVE crossings (best.cross > 0): the
      // crossing-free corpus keeps its byte-identical goldens (fully-open dog-mode
      // moved 4 suites — beyond the re-baseline budget)... with ONE finishing
      // exception (G-1-01391 item A, 2026-07-11): a CROSSING-FREE family may take a
      // widen when it leaves the family FULLY STRAIGHT (total dog < 0.05) — the
      // "small nudge" case where one prefix/suffix shift plus its cascade zeroes
      // every residual at once (01391: 32-33's −0.25 shift re-centres 19 and
      // 24/59 through the cascade). Structural corpus residuals (hub staircases,
      // solo-edge conventions, multi-defect families) can't reach a fully-straight
      // end state from one widen, so they never fire it. Kill switch
      // ENABLE_ROW_SUFFIX_WIDEN (default on).
      if (config.ENABLE_ROW_SUFFIX_WIDEN !== false)
        for (const cp of ordered) {
          if (cp.parents.length !== 2 || !cp.children.length) continue;
          const [pa, pb] = cp.parents;
          const g = pa.virtualY ?? 0;
          if ((pb.virtualY ?? 0) !== g) continue;
          const desired = childMidVX(cp) - unionVX(cp);
          if (Math.abs(desired) < 1e-6) continue;
          const dir = Math.sign(desired);
          const row = persons
            .filter((p) => (p.virtualY ?? 0) === g)
            .sort(
              (a, b) => a.virtualX - b.virtualX || (a.pid < b.pid ? -1 : 1)
            );
          const moverSet = new Set([pa.pid, pb.pid]);
          const idxs = [];
          row.forEach((p, i) => {
            if (moverSet.has(p.pid)) idxs.push(i);
          });
          if (idxs.length !== 2 || idxs[1] - idxs[0] !== 1) continue; // partners not adjacent
          const edgeIdx = dir > 0 ? idxs[1] + 1 : idxs[0] - 1;
          if (edgeIdx < 0 || edgeIdx >= row.length) continue; // room at the row end — A/B cover it
          const before = snapAll();
          // Per-couple anchors BEFORE the move, to detect what this candidate displaced.
          const preUnion = new Map(couples.map((c) => [c, unionVX(c)]));
          const preMid = new Map(couples.map((c) => [c, childMidVX(c)]));
          const spansBefore = marriageSpans(false);
          // (1) suffix shift on the couple's own row.
          const cut =
            dir > 0
              ? Math.min(pa.virtualX, pb.virtualX)
              : Math.max(pa.virtualX, pb.virtualX);
          for (const p of row)
            if (dir > 0 ? p.virtualX >= cut - 1e-9 : p.virtualX <= cut + 1e-9)
              p.virtualX += desired;
          const rowsTouched = new Set([g]);
          // Restore MIN clearance on a row by pushing along the move direction, walking
          // the row in its BEFORE order — monotone in that order, so the candidate can
          // never reorder anybody (lock-safe without relying on the orderOk gate).
          const enforceRow = (gy) => {
            const r = persons
              .filter((p) => (p.virtualY ?? 0) === gy)
              .sort(
                (a, b) =>
                  before.get(a.pid) - before.get(b.pid) ||
                  (a.pid < b.pid ? -1 : 1)
              );
            if (dir > 0) {
              for (let i = 1; i < r.length; i++)
                if (r[i].virtualX < r[i - 1].virtualX + MIN)
                  r[i].virtualX = r[i - 1].virtualX + MIN;
            } else {
              for (let i = r.length - 2; i >= 0; i--)
                if (r[i].virtualX > r[i + 1].virtualX - MIN)
                  r[i].virtualX = r[i + 1].virtualX - MIN;
            }
          };
          const gensAll = [
            ...new Set(persons.map((p) => p.virtualY ?? 0))
          ].sort((a, b) => a - b);
          // (2) DOWN: children follow their moved union anchor, shallowest row first.
          for (const r of gensAll) {
            if (r <= g) continue;
            const movedHere = new Set();
            for (const c2 of couples) {
              if (c2.parents.length !== 2 || !c2.children.length) continue;
              const childRows = new Set(
                c2.children.map((k) => k.virtualY ?? 0)
              );
              if (childRows.size !== 1 || !childRows.has(r)) continue;
              if (Math.abs(unionVX(c2) - preUnion.get(c2)) < 1e-9) continue; // anchor unmoved
              const d2 = unionVX(c2) - childMidVX(c2);
              if (Math.abs(d2) < 1e-9) continue;
              for (const k of c2.children) {
                k.virtualX += d2;
                movedHere.add(k.pid);
                // Carry each child's pendant founder spouse (no parents, single-married)
                // so its marriage doesn't wedge — its own children re-centre next row.
                for (const sp of k.spouses || []) {
                  if (sp.father || sp.mother) continue;
                  if ((sp.spouses || []).length !== 1) continue;
                  if ((sp.virtualY ?? 0) !== r || movedHere.has(sp.pid))
                    continue;
                  sp.virtualX += d2;
                  movedHere.add(sp.pid);
                }
              }
              rowsTouched.add(r);
            }
            if (rowsTouched.has(r)) enforceRow(r);
          }
          // (3) UP: single-married ancestor couples re-centre over their moved children.
          for (let gi = gensAll.indexOf(g) - 1; gi >= 0; gi--) {
            const r = gensAll[gi];
            for (const c2 of couples) {
              if (c2.parents.length !== 2 || !c2.children.length) continue;
              if (!c2.parents.every((p) => (p.virtualY ?? 0) === r)) continue;
              if (!c2.parents.every((p) => (p.spouses || []).length === 1))
                continue; // a hub's placement serves its other union too — leave it
              if (Math.abs(childMidVX(c2) - preMid.get(c2)) < 1e-9) continue;
              const d2 = childMidVX(c2) - unionVX(c2);
              if (Math.abs(d2) < 1e-9) continue;
              for (const p of c2.parents) p.virtualX += d2;
              rowsTouched.add(r);
            }
            if (rowsTouched.has(r)) enforceRow(r);
          }
          // Explicit same-row clearance (enforceRow guarantees it; belt and braces).
          const clear = [...rowsTouched].every((gy) => {
            const r = persons
              .filter((p) => (p.virtualY ?? 0) === gy)
              .sort((a, b) => a.virtualX - b.virtualX);
            for (let i = 1; i < r.length; i++)
              if (r[i].virtualX - r[i - 1].virtualX < MIN - 1e-6) return false;
            return true;
          });
          const cand = measure();
          // Crossing mode (the original gate) needs cross strictly <. Dog mode
          // (product rule) accepts cross == as long as the total dog-leg strictly
          // drops — widening freely is preferred over residual doglegs — AND the
          // straightening PAYS FOR ITSELF: the cascade re-centres children but
          // cannot carry a non-pendant spouse (its own parents pin it), so a bar
          // can stretch; total stretch must stay strictly below the dog-leg gain
          // (01391: 8-9 stretches 0.25 for a 1.0 gain — accepted; C1458's cap-
          // length bar stretching for a marginal gain — rejected).
          const spansAfter = marriageSpans(false);
          let stretchSum = 0;
          let stretchMax = 0;
          for (const [k2, len] of spansAfter) {
            const grow = Math.max(0, len - (spansBefore.get(k2) ?? len));
            stretchSum += grow;
            stretchMax = Math.max(stretchMax, grow);
          }
          const dogModeOk =
            cand.cross === best.cross &&
            stretchSum < best.dog - cand.dog - 1e-6 &&
            // Crossing-free families: finishing NUDGES only (see header note) —
            // the family must end FULLY straight and no single bar may do more
            // than drift (≤ 0.3 of a unit — 01391's cascade drifts wide 2-3 by
            // +0.25 and 18-19 by +0.04; C1396's candidate would LEAP its
            // cap-length 13-14 bar by over a unit to buy one straight drop, the
            // spouse-flinging the marriage caps exist to prevent).
            (best.cross > 0 || (cand.dog < 0.05 && stretchMax <= 0.3));
          if (
            clear &&
            (cand.cross < best.cross || dogModeOk) &&
            cand.over <= best.over &&
            cand.dog < best.dog - 1e-6 &&
            maxDogLeg() <= dogCap + 1e-6 &&
            orderOk()
          ) {
            best = cand;
            improved = true;
          } else restoreSnap(before);
        }

      // SOLO-child straighten — pull each single-parent child (carrying its own subtree)
      // so it sits straight UNDER its (coupled) parent. OBSOLETE under the contour
      // apportionment: _contourApportion now seats every solo child at its parent's-side
      // sibship EDGE (union still centred over the SHARED children). Pulling it dead-under
      // the parent here only TRADES the small edge drop for a worse union-over-shared
      // overhang — and reducing the (larger) solo drop nets a dog-leg "win" in this metric,
      // so it fires and de-centres the couple (13180: 48 dragged under 49, shared sib 1
      // flung left). Gated off; the contour placement is the better convention.
      if (!SOLO_PLACED_BY_CONTOUR)
        for (const s of soloDrops) {
          const shift = s.parent.virtualX - s.child.virtualX;
          if (Math.abs(shift) < 1e-9) continue;
          const before = snapAll();
          for (const pid of descendantsOf(s.child)) {
            byPidMap.get(pid).virtualX += shift;
          }
          resolveDownWiden();
          const cand = measure();
          if (
            cand.cross <= best.cross &&
            cand.dog < best.dog - 1e-6 &&
            maxDogLeg() <= dogCap + 1e-6
          ) {
            best = cand;
            improved = true;
          } else {
            restoreSnap(before);
          }
        }

      // Adjacent-SWAP sweep — fixes ORDER defects the position-only moves can't reach:
      // a child crossing to the wrong side of its parent (01366: 33 left of 31, but 8 is
      // right of 17) and a plain sibling wedged inside a sibling's marriage (01391: 57
      // between 6 and 7). For each adjacent same-row pair (that don't share a subtree —
      // i.e. not a couple/cross-marriage), exchange their x and carry their descendant
      // subtrees, widen to clear, and KEEP it only if the real crossing count drops (ties
      // → total dog-leg) without ballooning a drop. The detector gate rejects any swap
      // that would add a crossing, so dense cross-marriage families (01373) are protected.
      const swapGens = [...new Set(persons.map((p) => p.virtualY ?? 0))].sort(
        (a, b) => a - b
      );
      for (const g of swapGens) {
        const row = persons
          .filter((p) => (p.virtualY ?? 0) === g)
          .sort((a, b) => a.virtualX - b.virtualX || (a.pid < b.pid ? -1 : 1));
        for (let i = 0; i + 1 < row.length; i++) {
          const a = row[i];
          const b = row[i + 1];
          const da = descendantsOf(a);
          const db = descendantsOf(b);
          let shared = false;
          for (const pid of da) {
            if (db.has(pid)) {
              shared = true;
              break;
            }
          }
          if (shared) continue; // couple / cross-marriage — never swap apart
          const dx = b.virtualX - a.virtualX;
          if (dx <= 1e-9) continue;
          const before = snapAll();
          for (const pid of da) byPidMap.get(pid).virtualX += dx;
          for (const pid of db) byPidMap.get(pid).virtualX -= dx;
          resolveDownWiden();
          const cand = measure();
          const better =
            isBetterDog(cand, best) && cand.over <= best.over && orderOk();
          if (better && maxDogLeg() <= dogCap + 1e-6) {
            best = cand;
            improved = true;
            break; // row positions changed; recompute on the next sweep
          }
          restoreSnap(before);
        }
      }
      if (!improved) break;
    }

    // Readability FAN move — a plain sibling sitting between a married sibling and that
    // sibling's spouse reads as if it belongs to the in-law marriage. Slide it to the
    // married sibling's FAR side (01391: 57 between 6 and 7 → 57,6,7), carrying its
    // subtree. The apportionment leaves this dog-leg/crossing-neutral, so the move/swap
    // sweeps won't do it; here it is accepted whenever it does NOT add a crossing or
    // balloon a drop (a couple-centre move on the next sweep re-centres the parents over
    // the re-fanned sibship). Only fires on the exact wedged-plain-sibling pattern, so
    // it can't disturb a dense cross-marriage row.
    // FINAL readability pass (after the main loop settles, so couple-centring can't
    // undo it): slide a plain sibling out from between a married sibling and its
    // spouse, to the married sibling's far side — kept if it adds no crossing.
    const fanRows = [...new Set(persons.map((p) => p.virtualY ?? 0))].sort(
      (a, b) => a - b
    );
    const crossMarried = (q) =>
      (q.spouses || []).some((s) => s && (s.father || s.mother));
    for (const g of fanRows) {
      let guard = 0;
      let moved = true;
      while (moved && guard++ < 8) {
        moved = false;
        const row = persons
          .filter((p) => (p.virtualY ?? 0) === g)
          .sort((a, b) => a.virtualX - b.virtualX || (a.pid < b.pid ? -1 : 1));
        for (let i = 0; i < row.length && !moved; i++) {
          const m = row[i];
          const parent = m.father || m.mother;
          if (!parent || !crossMarried(m)) continue;
          const sibPids = new Set((parent.children || []).map((c) => c.pid));
          if (sibPids.size < 2) continue;
          const spouse = (m.spouses || []).find(
            (s) => s && (s.father || s.mother)
          );
          const sIdx = row.indexOf(spouse);
          if (sIdx < 0) continue;
          const lo = Math.min(i, sIdx);
          const hi = Math.max(i, sIdx);
          let p = null;
          for (let k = lo + 1; k < hi; k++) {
            if (sibPids.has(row[k].pid) && !crossMarried(row[k])) {
              p = row[k];
              break;
            }
          }
          if (!p) continue;
          const before = snapAll();
          const target = sIdx > i ? m.virtualX - MIN : m.virtualX + MIN;
          const shift = target - p.virtualX;
          for (const pid of descendantsOf(p))
            byPidMap.get(pid).virtualX += shift;
          resolveDownWiden();
          const cand = measure();
          if (
            cand.cross <= best.cross &&
            maxDogLeg() <= dogCap + 1e-6 &&
            orderOk()
          ) {
            best = cand;
            moved = true;
          } else {
            restoreSnap(before);
          }
        }
      }
    }

    // HALF-SIB regroup — bring a full sib back across so a wedged half-sib falls at the
    // sibship edge. OBSOLETE under the contour apportionment (which already seats solo
    // children at their parent's-side edge with the full sibs grouped); gated off because,
    // paired with the solo-drop straighten, it shoved 13180's shared sib 1 far left.
    if (!SOLO_PLACED_BY_CONTOUR)
      for (const g of fanRows) {
        let guard = 0;
        let moved = true;
        while (moved && guard++ < 8) {
          moved = false;
          const row = persons
            .filter((p) => (p.virtualY ?? 0) === g)
            .sort(
              (a, b) => a.virtualX - b.virtualX || (a.pid < b.pid ? -1 : 1)
            );
          for (let i = 0; i < row.length && !moved; i++) {
            const s = row[i];
            const P =
              s.father && !s.mother
                ? s.father
                : s.mother && !s.father
                ? s.mother
                : null;
            if (!P) continue; // 0 or 2 parents — not a solo child
            const sp = (P.spouses || []).find(Boolean);
            if (!sp) continue; // uncoupled parent — ordinary single-parent sibship
            const fullSibs = (P.children || []).filter((c) =>
              (sp.children || []).some((cc) => cc.pid === c.pid)
            );
            const fIdx = fullSibs
              .map((c) => row.indexOf(c))
              .filter((k) => k >= 0);
            if (fIdx.length < 1) continue;
            if (i <= Math.min(...fIdx) || i >= Math.max(...fIdx)) continue; // not wedged
            // s is between full sibs. Move the full sib(s) on P's side of s across to the
            // far side so they rejoin and s lands at P's edge.
            const pSide = (P.virtualX ?? 0) >= (sp.virtualX ?? 0) ? 1 : -1;
            const toMove = fullSibs.filter((c) => {
              const k = row.indexOf(c);
              return pSide > 0 ? k > i : k < i;
            });
            if (!toMove.length) continue;
            // Place the wedged full sib(s) PAST the other full sibs (the far edge from P),
            // so the half-sib stays at the P-side edge straight under its parent.
            const others = fullSibs
              .filter((c) => !toMove.includes(c))
              .map((c) => c.virtualX);
            if (!others.length) continue;
            let edge = pSide > 0 ? Math.min(...others) : Math.max(...others);
            const before = snapAll();
            for (const c of toMove.sort((a, b) =>
              pSide > 0 ? b.virtualX - a.virtualX : a.virtualX - b.virtualX
            )) {
              edge += pSide > 0 ? -MIN : MIN;
              const shift = edge - c.virtualX;
              for (const pid of descendantsOf(c))
                byPidMap.get(pid).virtualX += shift;
            }
            resolveDownWiden();
            const cand = measure();
            if (cand.cross <= best.cross && maxDogLeg() <= dogCap + 1e-6) {
              best = cand;
              moved = true;
            } else {
              restoreSnap(before);
            }
          }
        }
      }

    // SOLO-DISAMBIGUATE (final): a coupled parent with a solo child that sits directly above
    // one of its SHARED children has its solo drop graze the union's shared bar, so the
    // half-sib reads as a shared child (13180: 49 over shared sib 1, its drop to solo 48
    // touching the bar). The contour can't avoid it — MIN > HALF means the straddling parent
    // always sits within its shared children's span. Nudge the parent OUTWARD toward its
    // solo child, to between the outermost shared child and the solo child, so the solo drop
    // clears the bar. KEPT only if it adds NO crossing and NO marriage-over-node (so the
    // stretched marriage never runs over a node — the parallel-line defect the user weights
    // heavily); the small union dog-leg it opens is the accepted trade. Families where the
    // stretch WOULD cross a node (e.g. dense cross-marriage rows) are left as-is by the gate.
    for (const s of soloDrops) {
      const P = s.parent;
      const S = s.child;
      const cp = couples.find(
        (c) => c.parents.length === 2 && c.parents.some((p) => p.pid === P.pid)
      );
      if (!cp || !cp.children.length) continue;
      const sharedXs = cp.children.map((c) => c.virtualX);
      if (!sharedXs.some((x) => Math.abs(x - P.virtualX) < 1e-6)) continue; // not above a shared child
      const dir = S.virtualX > P.virtualX ? 1 : -1;
      const outer = dir > 0 ? Math.max(...sharedXs) : Math.min(...sharedXs);
      const shift = (outer + S.virtualX) / 2 - P.virtualX;
      if (shift * dir <= 1e-6) continue; // only ever nudge OUTWARD toward the solo child
      const before = snapAll();
      P.virtualX += shift;
      resolveDownWiden();
      const cand = measure();
      if (cand.cross <= best.cross && cand.over <= best.over && orderOk())
        best = cand;
      else restoreSnap(before);
    }

    // HUB-FLANK de-wedge: a multi-spouse hub should sit BETWEEN its spouses (S | hub | S) so
    // no marriage line runs over a spouse. The contour places each spouse over its own
    // children, which can leave BOTH spouses on the same side of the hub — then one spouse's
    // marriage spans over the other (C1374: 3-4 over 9; CHUB1: 4-9 over 3, both spouses left
    // of hub 4). For each hub with all its same-row spouses bunched on one side, move the
    // LIGHTEST spouse (fewest descendants, carrying its subtree) to the empty side, adjacent
    // to the hub. KEPT only if it REDUCES the marriage-over-node count without adding a
    // crossing (the small union dog-leg the moved spouse opens is the accepted trade).
    const sameRowSpouses = (p) =>
      (p.spouses || []).filter((s) => (s.virtualY ?? 0) === (p.virtualY ?? 0));
    for (const H of persons) {
      const sps = sameRowSpouses(H);
      if (sps.length < 2) continue;
      const left = sps.filter((s) => s.virtualX < H.virtualX - 1e-6);
      const right = sps.filter((s) => s.virtualX > H.virtualX + 1e-6);
      if (!(left.length === 0) === !(right.length === 0)) continue; // already on both sides
      const bunched = left.length ? left : right;
      const dir = left.length ? 1 : -1; // move toward the EMPTY side
      const light = bunched.reduce((a, b) =>
        descendantsOf(b).size < descendantsOf(a).size ? b : a
      );
      const shift = H.virtualX + dir * MIN - light.virtualX;
      if (Math.abs(shift) < 1e-9) continue;
      const before = snapAll();
      for (const pid of descendantsOf(light))
        byPidMap.get(pid).virtualX += shift;
      resolveDownWiden();
      const cand = measure();
      if (cand.over < best.over && cand.cross <= best.cross && orderOk())
        best = cand;
      else restoreSnap(before);
    }

    // COUPLE de-wedge (G-1-01411): a plain same-row couple whose marriage bar runs OVER an
    // unrelated free-leaf node — e.g. sibling 33 wedged between 32 and his wife 60 when the
    // bidirectional contour seats a married-in spouse OUTSIDE a sibship whose partner is
    // not the edge sibling. At 0 crossings the reorder search has no gradient to fix it
    // (a childless, unmarried leaf carries no edges the objective can see), and the
    // HUB-FLANK pass above only covers multi-spouse hubs — so the wedge survives to render
    // as a below-row lane for the marriage bar. When EVERY in-between node is a free leaf
    // (no children, no spouses — pure-x movable, nothing to carry), PERMUTE the occupants
    // of the exact slot multiset [leaves..., one partner]: the partner takes the slot
    // adjacent to its spouse, the leaves shift outward preserving their order. A pure
    // permutation moves nobody else, grows no width and needs no re-clearance — the first
    // cut of this pass parked the leaves via insert+widen, and resolveDownWiden's one-slot
    // rightward push knocked SEVEN unrelated unions off-centre by a full unit each (the
    // 2026-07-11 dog-leg regression). KEPT only if the marriage-over-node count strictly
    // DROPS without adding a crossing (an over-free family can never fire it), within a
    // bounded dog allowance (un-wedging inherently widens the leaves' own sibship span by
    // one slot — worth it: a bar over a node reads worse than a half-unit drop) + dog-cap
    // + orderOk (a locked order self-rejects the reorder). Both partners are tried as the
    // mover; the better accepted candidate wins.
    {
      const seenW = new Set();
      for (const p of persons) {
        for (const sp of p.spouses || []) {
          const key = pairKey(p.pid, sp.pid);
          if (seenW.has(key)) continue;
          seenW.add(key);
          if (overBridges.has(key)) continue; // intentional lane — not a defect
          if ((p.virtualY ?? 0) !== (sp.virtualY ?? 0)) continue;
          const lo = Math.min(p.virtualX, sp.virtualX);
          const hi = Math.max(p.virtualX, sp.virtualX);
          if (hi - lo < MIN + 1e-9) continue; // adjacent couple — nothing fits between
          const wedged = persons
            .filter(
              (q) =>
                q !== p &&
                q !== sp &&
                (q.virtualY ?? 0) === (p.virtualY ?? 0) &&
                q.virtualX > lo + 1e-6 &&
                q.virtualX < hi - 1e-6
            )
            .sort((a, b) => a.virtualX - b.virtualX);
          if (!wedged.length) continue;
          if (
            wedged.some(
              (w) => (w.children || []).length || (w.spouses || []).length
            )
          )
            continue; // something structural is wedged — not this pass's move
          const dogAllow = 0.5 * wedged.length + 1e-6;
          const partners = [p, sp].sort((a, b) => a.virtualX - b.virtualX);
          let accepted = null; // { snap, cand }
          const baseline = snapAll();
          for (const mover of [partners[1], partners[0]]) {
            restoreSnap(baseline);
            // Slot multiset = the wedged leaves' slots + the mover's slot.
            const slots = [
              ...wedged.map((w) => w.virtualX),
              mover.virtualX
            ].sort((a, b) => a - b);
            if (mover === partners[1]) {
              // Right partner slides INWARD to the lowest slot (adjacent to its
              // spouse); leaves take the remaining slots rightward, in order.
              mover.virtualX = slots[0];
              wedged.forEach((w, i) => {
                w.virtualX = slots[i + 1];
              });
            } else {
              // Left partner takes the highest slot; leaves shift leftward.
              mover.virtualX = slots[slots.length - 1];
              wedged.forEach((w, i) => {
                w.virtualX = slots[i];
              });
            }
            const cand = measure();
            if (
              cand.over < best.over &&
              cand.cross <= best.cross &&
              cand.dog <= best.dog + dogAllow &&
              maxDogLeg() <= dogCap + 1e-6 &&
              orderOk() &&
              (!accepted ||
                cand.cross < accepted.cand.cross ||
                (cand.cross === accepted.cand.cross &&
                  cand.dog < accepted.cand.dog - 1e-6))
            ) {
              accepted = { snap: snapAll(), cand };
            }
          }
          if (accepted) {
            restoreSnap(accepted.snap);
            family.convertToCoordinates();
            best = accepted.cand;
          } else {
            restoreSnap(baseline);
          }
        }
      }
    }

    // HUB-REMARRIAGE straighten (TGCLOUD-1456): a multi-spouse hub's SECOND (remarriage)
    // union drops its child from the union midpoint, but the shared hub node is pinned by
    // its OTHER union, so the main hill-climb (which moves PARENTS) oscillates to a
    // compromise dog-leg (node 10 under hub 3, not under union 3-8). Here we instead move
    // the union's CHILDREN (carrying their subtrees) under the union midpoint. KEPT only if
    // it adds NO crossing and NO marriage-over-node and net-reduces the dog-leg — so it is
    // corpus-safe by construction, and a no-op where there's no room (a wide-sibship
    // interleave needs a larger relocation, left as-is by the gate). Flag-gated.
    if (config.ENABLE_HUB_REMARRIAGE_STRAIGHTEN)
      for (const H of persons) {
        if (sameRowSpouses(H).length < 2) continue; // hubs only
        for (const cp of couples) {
          if (cp.parents.length !== 2 || !cp.children.length) continue;
          if (!cp.parents.some((p) => p.pid === H.pid)) continue;
          const shift = unionVX(cp) - childMidVX(cp);
          if (Math.abs(shift) < 1e-3) continue; // already straight
          const before = snapAll();
          // Per-union offsets BEFORE, so we can reject any move that straightens this union
          // at the cost of worsening ANOTHER (a wide primary sibship shift pushing the other
          // union's child off via resolveDownWiden — the 01401 conflict).
          const offBefore = couples.map((c) =>
            Math.abs(unionVX(c) - childMidVX(c))
          );
          const movers = new Set();
          for (const c of cp.children)
            for (const pid of descendantsOf(c)) movers.add(pid);
          for (const pid of movers) byPidMap.get(pid).virtualX += shift;
          resolveDownWiden();
          const cand = measure();
          const noUnionWorse = couples.every(
            (c, i) =>
              Math.abs(unionVX(c) - childMidVX(c)) <= offBefore[i] + 1e-6
          );
          if (
            cand.cross <= best.cross &&
            cand.over <= best.over &&
            cand.dog < best.dog - 1e-6 &&
            noUnionWorse &&
            maxDogLeg() <= dogCap + 1e-6 &&
            orderOk()
          )
            best = cand;
          else restoreSnap(before);
        }
      }

    // PLAIN-COUPLE children recentre (G-1-01411, 2026-07-11 — product rule: "where they've
    // got space around them there's no reason for doglegs; go WIDER and centre the children
    // under the parents"). The main hill-climb moves PARENTS toward their children, so a
    // couple whose parents are pinned (e.g. 55 under its own parents' staircase, or 63-64
    // seated by the sibship convention) keeps a residual drop the parent-side moves can
    // never fix — while the CHILDREN often have free space (bottom rows, row edges). The
    // exact hub-remarriage move above, for NON-hub couples (both partners single-married,
    // so the two passes are disjoint and the hub pass's behaviour is byte-identical):
    // shift the union's children (carrying their subtrees) under the union anchor,
    // widening via resolveDownWiden as needed — width growth is the preferred cost.
    // Acceptance per the product rule: crossings ≤, marriage-over-node ≤, total dog-leg
    // strictly <, no other union worsened, dog-cap, orderOk.
    {
      if (config.ENABLE_HUB_REMARRIAGE_STRAIGHTEN)
        for (const cp of couples) {
          if (cp.parents.length !== 2 || !cp.children.length) continue;
          if (!cp.parents.every((p) => (p.spouses || []).length === 1))
            continue; // hubs → pass above
          const shift = unionVX(cp) - childMidVX(cp);
          if (Math.abs(shift) < 1e-3) continue; // already straight
          const before = snapAll();
          const offBefore = couples.map((c) =>
            Math.abs(unionVX(c) - childMidVX(c))
          );
          const spansBefore = marriageSpans(true);
          const movers = new Set();
          for (const c of cp.children) {
            for (const pid of descendantsOf(c)) movers.add(pid);
            // Carry each child's pendant founder spouse (no parents,
            // single-married, same row) so its marriage travels intact — the
            // same convention as ROW-SUFFIX WIDEN's cascade.
            for (const sp2 of c.spouses || []) {
              if (sp2.father || sp2.mother) continue;
              if ((sp2.spouses || []).length !== 1) continue;
              if ((sp2.virtualY ?? 0) !== (c.virtualY ?? 0)) continue;
              movers.add(sp2.pid);
            }
          }
          for (const pid of movers) byPidMap.get(pid).virtualX += shift;
          resolveDownWiden();
          const cand = measure();
          const noUnionWorse = couples.every(
            (c, i) =>
              Math.abs(unionVX(c) - childMidVX(c)) <= offBefore[i] + 1e-6
          );
          const spansAfter = marriageSpans(true);
          const noMarriageStretched = [...spansAfter].every(
            ([k2, len]) => len <= (spansBefore.get(k2) ?? len) + 1e-6
          );
          if (
            cand.cross <= best.cross &&
            cand.over <= best.over &&
            cand.dog < best.dog - 1e-6 &&
            noUnionWorse &&
            noMarriageStretched &&
            maxDogLeg() <= dogCap + 1e-6 &&
            orderOk()
          )
            best = cand;
          else restoreSnap(before);
        }
    }

    // ASYM-JUNCTION PARK (G-1-01409 node 9, 2026-07-10): the straightening machinery above
    // holds a hub-remarriage union's MIDPOINT over its children, so whenever the hub is
    // pushed aside (yield, widening, re-separation) the free spouse gets MIRRORED outward
    // by the same amount — flung far from the family for no informational gain. Desktop
    // convention (per Kathryn's exports) does not require the symmetric bar: the junction
    // may sit ASYMMETRICALLY over the child column, with only the hub-side bar segment
    // stretched. So, as the LAST pass (nothing after can re-fling), pull the free
    // single-married spouse (carrying its pure tower) back to the standard partner offset
    // from the child column and mark the union — unionVX above and buildRenderModel then
    // both anchor its junction at the children's midpoint. Gated on nothing measurable
    // worsening (cross/over/dog Pareto, explicit same-row clearance) + orderOk.
    const PARK_OFF = 0.5; // layoutV2's COUPLE_HALF_GAP — the normal partner→union offset
    if (config.ENABLE_ASYM_JUNCTION_PARK !== false)
      for (const cp of couples) {
        if (cp.parents.length !== 2 || !cp.children.length) continue;
        const [a, b] = cp.parents;
        const hub =
          (a.spouses || []).length >= 2 && (b.spouses || []).length === 1
            ? a
            : (b.spouses || []).length >= 2 && (a.spouses || []).length === 1
            ? b
            : null;
        if (!hub) continue; // needs exactly (hub, free single-married spouse)
        const S = a.pid === hub.pid ? b : a;
        if (extraPrevSpouse(hub, S)) continue; // extra-spouse union — has its own anchor
        if ((S.virtualY ?? 0) !== (hub.virtualY ?? 0)) continue;
        const mid = childMidVX(cp);
        const lo = Math.min(S.virtualX, hub.virtualX);
        const hi = Math.max(S.virtualX, hub.virtualX);
        if (!(mid > lo + 1e-9 && mid < hi - 1e-9)) continue; // junction can't reach the children
        const dir = S.virtualX < hub.virtualX ? -1 : 1; // S's side of the bar
        const target = mid + dir * PARK_OFF;
        const flung = (S.virtualX - target) * dir; // how far PAST standard S sits
        if (flung <= 0.25) continue; // standard-ish spacing — not the defect
        const tower = pureTowerOf(S);
        if (!tower) continue; // entangled ancestry — S can't move alone
        // Only a BARE spouse (a lone founder, no ancestor tower) gets parked. A
        // tower-bearing spouse's position is co-determined by its tower — e.g. the 1468c
        // edge-straddle deliberately mirrors G-1-01401's co-parent 2 and seats spouse 8
        // for its tower straddle; parking those undoes an intentional arrangement.
        if (tower.size !== 1) continue;
        const shift = target - S.virtualX;
        // Explicit same-row clearance for every moved node: an inward move can't be caught
        // by the Pareto gates (an overlap is neither a crossing nor a marriage-over-node).
        let clear = true;
        for (const pid of tower) {
          const mv = byPidMap.get(pid);
          const nx = mv.virtualX + shift;
          for (const q of persons) {
            if (tower.has(q.pid)) continue;
            if ((q.virtualY ?? 0) !== (mv.virtualY ?? 0)) continue;
            if (Math.abs(q.virtualX - nx) < MIN - 1e-9) {
              clear = false;
              break;
            }
          }
          if (!clear) break;
        }
        if (!clear) continue;
        const before = snapAll();
        const key = pairKey(a.pid, b.pid);
        for (const pid of tower) byPidMap.get(pid).virtualX += shift;
        family.asymJunctionsV3.add(key); // mark BEFORE measuring — metric and render agree
        resolveDownWiden();
        const cand = measure();
        if (
          cand.cross <= best.cross &&
          cand.over <= best.over &&
          cand.dog <= best.dog + 1e-6 &&
          maxDogLeg() <= dogCap + 1e-6 &&
          orderOk()
        )
          best = cand;
        else {
          family.asymJunctionsV3.delete(key);
          restoreSnap(before);
        }
      }

    // SOLO-PARK (13180/C1366 nodes 8→33 and 49→48, same review wave): a COUPLED parent's
    // single-parent child is seated at the parent's-side edge of the shared sibship, which
    // leaves a half-unit solo drop by construction (the couple straddles its SHARED
    // children) — and SOLO-DISAMBIGUATE's midway nudge (49 to (1+48)/2) trades a 0.25
    // union dog-leg for the cleared graze. The asymmetric junction dissolves the whole
    // trade: move the PARENT directly over its solo child and anchor its union's junction
    // over the SHARED children — both the solo drop AND the union land at 0. Single-solo,
    // single-married parents only (a hub parent's placement is deliberate; two solo
    // columns can't both be under one parent).
    if (config.ENABLE_ASYM_JUNCTION_PARK !== false)
      for (const s of soloDrops) {
        const P = s.parent;
        const S = s.child;
        if ((P.spouses || []).length !== 1) continue;
        if (soloDrops.filter((d) => d.parent.pid === P.pid).length > 1)
          continue;
        const off = S.virtualX - P.virtualX;
        if (Math.abs(off) < 1e-6) continue; // already straight
        const cp = couples.find(
          (c) =>
            c.parents.length === 2 && c.parents.some((q) => q.pid === P.pid)
        );
        if (!cp || !cp.children.length) continue;
        const sp = cp.parents.find((q) => q.pid !== P.pid);
        if ((S.virtualY ?? 0) !== (P.virtualY ?? 0) + 1) continue; // not a plain one-row drop
        const mid = childMidVX(cp);
        const nx = S.virtualX;
        // The junction must still be able to sit over the shared children afterwards.
        const lo2 = Math.min(nx, sp.virtualX);
        const hi2 = Math.max(nx, sp.virtualX);
        if (!(mid > lo2 + 1e-9 && mid < hi2 - 1e-9)) continue;
        // Same-row clearance at the target column (P moves alone — children stay put).
        let clear = true;
        for (const q of persons) {
          if (q.pid === P.pid) continue;
          if ((q.virtualY ?? 0) !== (P.virtualY ?? 0)) continue;
          if (Math.abs(q.virtualX - nx) < MIN - 1e-9) {
            clear = false;
            break;
          }
        }
        if (!clear) continue;
        const before = snapAll();
        const key = pairKey(cp.parents[0].pid, cp.parents[1].pid);
        const hadKey = family.asymJunctionsV3.has(key);
        P.virtualX = nx;
        family.asymJunctionsV3.add(key); // metric + render anchor over the SHARED children
        // If P was its own sibship's OUTERMOST child, the park drags every ancestor
        // union's child-midpoint (u(42,43) in 13180 drifts 0.25 and the dog gate would
        // reject). Plain same-row 1×1 ancestor couples simply FOLLOW the drift — shifting
        // a couple keeps its own union straight and passes the drift one level up, dying
        // out at founders.
        const movedPids = new Set([P.pid]);
        {
          const chained = new Set();
          let frontier = [P];
          let guard = 0;
          while (frontier.length && guard++ < 20) {
            const next = [];
            for (const cur of frontier) {
              if (!cur.father || !cur.mother) continue;
              const cp2 = couples.find(
                (c) =>
                  c.parents.length === 2 &&
                  c.parents.some((q) => q.pid === cur.father.pid) &&
                  c.parents.some((q) => q.pid === cur.mother.pid)
              );
              if (!cp2 || chained.has(cp2)) continue;
              chained.add(cp2);
              const [pa, pb] = cp2.parents;
              if ((pa.virtualY ?? 0) !== (pb.virtualY ?? 0)) continue;
              if ((pa.spouses || []).length !== 1) continue; // hubs/anchored unions:
              if ((pb.spouses || []).length !== 1) continue; // let the gates decide
              const d = childMidVX(cp2) - unionVX(cp2);
              if (Math.abs(d) < 1e-9) continue;
              pa.virtualX += d;
              pb.virtualX += d;
              movedPids.add(pa.pid);
              movedPids.add(pb.pid);
              next.push(pa, pb);
            }
            frontier = next;
          }
        }
        // Same-row clearance for every chained ancestor (the pre-check above covered P).
        let chainClear = true;
        for (const pid of movedPids) {
          const mv = byPidMap.get(pid);
          for (const q of persons) {
            if (movedPids.has(q.pid)) continue;
            if ((q.virtualY ?? 0) !== (mv.virtualY ?? 0)) continue;
            if (Math.abs(q.virtualX - mv.virtualX) < MIN - 1e-9) {
              chainClear = false;
              break;
            }
          }
          if (!chainClear) break;
        }
        if (!chainClear) {
          if (!hadKey) family.asymJunctionsV3.delete(key);
          restoreSnap(before);
          continue;
        }
        resolveDownWiden();
        const cand = measure();
        if (
          cand.cross <= best.cross &&
          cand.over <= best.over &&
          cand.dog <= best.dog + 1e-6 &&
          maxDogLeg() <= dogCap + 1e-6 &&
          orderOk()
        )
          best = cand;
        else {
          if (!hadKey) family.asymJunctionsV3.delete(key);
          restoreSnap(before);
        }
      }

    // HUB-ASYM ANCHOR (G-1-01416, 2026-07-11 — Tim's ruling, verbatim intent:
    // "the asymmetry we accepted in the spacing between parents to preference
    // straight drops applies to multiple spouse scenarios too"). A multi-spouse
    // hub's union anchors — the partner midpoint for its primary union, the
    // prevSpouse ± EXTRA_SPOUSE_CHILD_X_STEP staircase for extra spouses — are
    // convention points that the children then compromise toward; where the
    // compromise leaves a residual drop, re-anchor the JUNCTION over the
    // children's midpoint instead, clamped along the (possibly diagonal)
    // partner bar. Nothing moves — only the union node slides along the bar
    // (an asymmetric bar, the same accepted trade as the park pass and the
    // 1468c edge-straddle). Runs LAST (all node positions are final); gated on
    // nothing measurable worsening + the residual strictly shrinking. Covers
    // the primary union of a 2-spouse hub (01416's 25-29: partners spread by
    // tower clearance, children mid at t=0.7 along the bar) and 3+-spouse
    // staircase unions (C1458's 15-19 / 15-42) alike. Fixed-order clamp keeps
    // an out-of-reach midpoint (t outside [0.05, 0.95]) a PARTIAL improvement
    // — accepted only when the dog metric still strictly drops.
    if (config.ENABLE_HUB_ASYM_ANCHOR !== false)
      for (const cp of couples) {
        if (cp.parents.length !== 2 || !cp.children.length) continue;
        if (!cp.parents.some((q) => (q.spouses || []).length >= 2)) continue; // hubs only
        const key = pairKey(cp.parents[0].pid, cp.parents[1].pid);
        if (family.asymJunctionsV3.has(key)) continue; // already anchored (park/solo)
        if (Math.abs(unionVX(cp) - childMidVX(cp)) < 1e-3) continue; // straight already
        family.asymJunctionsV3.add(key); // metric + render agree while measuring
        const cand = measure();
        if (
          cand.cross <= best.cross &&
          cand.over <= best.over &&
          cand.dog < best.dog - 1e-6 &&
          maxDogLeg() <= dogCap + 1e-6 &&
          orderOk()
        )
          best = cand;
        else family.asymJunctionsV3.delete(key);
      }

    // IN-LAW TOWER OVER-CHILD (G-1-01390 / G-1-01392, 2026-07-11): a married-in
    // in-law's ancestor tower is anchored to the family ONLY through the drop to
    // its child, but the packing can leave each tower couple's union OFF its
    // child — the child's ascent then goes up → hop → HORIZONTAL JOG along the
    // row bars → hop → up (Tim's "double hopover"; a plain vertical hop over a
    // bar is fine). Node-moving remedies all fail here: the couple sweep's
    // clearance pushes carry the very descent being centred (cancellation), and
    // rigid slides ripple through neighbouring hub families (measured: 01401 /
    // CHUB2 regressions). The junction alone is enough — per Tim's standing
    // ruling ("the asymmetry we accepted in the spacing between parents to
    // preference straight drops applies to multiple spouse scenarios too"),
    // re-anchor the TOWER COUPLE's junction over its children's midpoint along
    // the bar: union.x lands exactly over the child wherever the child is
    // within the bar's span, and at the bar's end when beyond (01390's 33-34,
    // whose child 30 is pinned 0.46 past the bar by the 29/39/30/38 co-parent
    // interleave — a documented order-level residual). A TOWER COUPLE,
    // recursively: both partners single-married and each either a founder (no
    // parents) or the ONLY child of another tower couple — the union's drop is
    // the couple's sole anchor to the rest of the family. Hub couples are the
    // HUB-ASYM ANCHOR's job above; everything else keeps its midpoint junction.
    if (config.ENABLE_INLAW_TOWER_OVER_CHILD !== false) {
      const isTowerPerson = (q, depth = 0) => {
        if ((q.spouses || []).length !== 1) return false;
        if (!q.hasParents) return true;
        if (depth > 6) return false;
        const f = q.father;
        const m = q.mother;
        if (!f || !m) return false;
        const sibs = persons.filter(
          (r) => r.fatherId === q.fatherId && r.motherId === q.motherId
        );
        if (sibs.length !== 1) return false;
        return isTowerPerson(f, depth + 1) && isTowerPerson(m, depth + 1);
      };
      for (const cp of couples) {
        if (cp.parents.length !== 2 || !cp.children.length) continue;
        if (
          !cp.parents.every(
            (q) =>
              isTowerPerson(q) &&
              (q.virtualY ?? 0) === (cp.parents[0].virtualY ?? 0)
          )
        )
          continue;
        const key = pairKey(cp.parents[0].pid, cp.parents[1].pid);
        if (family.asymJunctionsV3.has(key)) continue;
        if (Math.abs(unionVX(cp) - childMidVX(cp)) < 1e-3) continue; // straight
        family.asymJunctionsV3.add(key); // metric + render agree while measuring
        const cand = measure();
        if (
          cand.cross <= best.cross &&
          cand.over <= best.over &&
          cand.dog < best.dog - 1e-6 &&
          maxDogLeg() <= dogCap + 1e-6 &&
          orderOk()
        )
          best = cand;
        else family.asymJunctionsV3.delete(key);
      }
    }

    // MALE-LEFT NORMALISATION (SS03 / G-1-01429-2, 2026-07-12 — Tim's rule 3:
    // "standard nomenclature prefers the MALE on the LEFT of a couple; breaking
    // it is only acceptable when it buys real layout quality, never
    // gratuitously"). The crossing-minimising reorder search is orientation-
    // blind: a hill-climb step that flips a couple's sides can be kept when its
    // benefit is later superseded, leaving a female-left couple that a mirror
    // flip would restore for free (01429-2's both-ancestried 3-2 / 32-31 —
    // every metric ties either way). Rather than entangle the search, sweep the
    // settled result LAST: for each female-left couple, swap the partners'
    // columns and translate each partner's PURE ancestor tower rigidly with it
    // (translation, not mirror — mirroring would flip the tower's own couples
    // female-left). Adopt only when provably FREE: same-row clearance holds and
    // nothing measurable worsens (cross/over/dog Pareto + dog cap). Hubs are
    // skipped (spouse|hub|spouse flanking is its own convention), as are
    // containment-bridge couples (their sides are the decoupling machinery's)
    // and consanguineous partner pairs sharing a tower (a flip could tear the
    // shared ancestry). Unlocked only: under a locked order the user's
    // arrangement wins (and orderOk would reject the reorder anyway).
    if (config.ENABLE_COUPLE_ORIENTATION_NORMALISE !== false && !lockOrder) {
      for (const cp of ordered) {
        if (cp.parents.length !== 2) continue;
        const [pa, pb] = cp.parents;
        if ((pa.virtualY ?? 0) !== (pb.virtualY ?? 0)) continue;
        if ((pa.spouses || []).length !== 1) continue; // hub flanking
        if ((pb.spouses || []).length !== 1) continue;
        if (overBridges.has(pairKey(pa.pid, pb.pid))) continue;
        const left = pa.virtualX <= pb.virtualX ? pa : pb;
        const right = left === pa ? pb : pa;
        if (left.gender !== 'female' || right.gender !== 'male') continue;
        const towerL = pureTowerOf(left) || new Set([left.pid]);
        const towerR = pureTowerOf(right) || new Set([right.pid]);
        let shared = false;
        for (const pid of towerL) if (towerR.has(pid)) shared = true;
        if (shared) continue; // common ancestor — not provably free
        const delta = right.virtualX - left.virtualX;
        const before = snapAll();
        for (const pid of towerL) byPidMap.get(pid).virtualX += delta;
        for (const pid of towerR) byPidMap.get(pid).virtualX -= delta;
        // Same-row clearance for every moved pid — against unmoved neighbours
        // AND the other tower (the swapped regions can differ in width).
        const moved = new Set([...towerL, ...towerR]);
        let clear = true;
        for (const pid of moved) {
          const mv = byPidMap.get(pid);
          for (const q of persons) {
            if (q.pid === pid) continue;
            if ((q.virtualY ?? 0) !== (mv.virtualY ?? 0)) continue;
            if (Math.abs(q.virtualX - mv.virtualX) < MIN - 1e-9) {
              clear = false;
              break;
            }
          }
          if (!clear) break;
        }
        if (!clear) {
          restoreSnap(before);
          continue;
        }
        const cand = measure();
        if (
          cand.cross <= best.cross &&
          cand.over <= best.over &&
          cand.dog <= best.dog + 1e-6 &&
          maxDogLeg() <= dogCap + 1e-6 &&
          orderOk()
        )
          best = cand;
        else restoreSnap(before);
      }
    }
    family.convertToCoordinates();
  } catch (e) {
    family.asymJunctionsV3 = new Set();
    restoreAll();
  }
}
